import { useCallback, useEffect, useRef, useState } from "react";
import { FormInputHandle, GenericModalType } from "../utils/Types";
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Grid, ThemeProvider, Tooltip } from "@mui/material";
import { DataFieldInput } from "./DataForm";
import {
    Save as SaveIcon,
    CancelOutlined as CancelIcon,
} from '@mui/icons-material';
import { useStateContext } from "../reducers";
import FormModalPaperComponent from "./FormModalPaperComponent";
import { boxInputStyle, buttonTheme } from "../utils/Common";

const boxInputContainerStyle = {padding: "10px 20px 20px 20px !important"};

function GenericModal<T>(props: GenericModalType) {
    const {
        title,
        data,
        fields,
        apiCaller,
        onClose,
        hideDefaultCommands,
        customFormCommands,
        closeActionCaption,
        onSave,
        saveActionCaption,
        onValidate,
    } = props;

    const [canSave, setCanSave] = useState<boolean>(false);
    const [currentFocusedField, setCurrentFocusedField] = useState<string | null>(fields[0]?.name ?? null);
    const {menuState} = useStateContext();

    const formRef = useRef<FormInputHandle<T>>({
        getData: () => data,
        validationResult: false,
    });
    const canSaveRef = useRef<boolean>(false);

    const handleClose = useCallback((reason: string, onClose: Function) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown")
            return;

        onClose();
    }, [onClose]);

    const handleSave = useCallback(() => {
        const dataToSend = formRef.current?.getData();
        const latestCanSave = formRef.current?.validationResult;

        if (!latestCanSave)
            return;

        if (!onValidate)
        {
            onSave(dataToSend);
            return;
        }

        onValidate(dataToSend, onSave);
    }, [formRef, onSave, onValidate]);
    
    useEffect(() => {
        const interval = setInterval(() => {
            if (canSaveRef.current === (formRef.current?.validationResult ?? false))
                return;

            canSaveRef.current = formRef.current?.validationResult ?? false;
            // console.log({canSave: canSaveRef.current});
            setCanSave(canSaveRef.current);
            setCurrentFocusedField(formRef.current?.currentFocusedField ?? null);
        }, 250);
      
        return () => clearInterval(interval);
    }, []);

    // useEffect(() => console.log({canSave}), [canSave]);

    return (
        <Dialog
            open={true}
            onClose={(e, reason) => handleClose(reason, onClose)}
            PaperComponent={(props) => FormModalPaperComponent(props, menuState)}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
        >
            <DialogTitle sx={{cursor: 'move', backgroundColor: "#444", color: "#f1f1f1", display: "flex", justifyContent: "space-between", alignItems: "center"}} id="draggable-dialog-title">
            {
                title
            }
                <ThemeProvider theme={buttonTheme}>
                    <ButtonGroup>
                        <Tooltip
                            title={closeActionCaption ?? "Batal"}
                            arrow
                        >
                            <Button
                                variant="contained"
                                onClick={() => onClose()}
                                color="dark"
                            >
                                <CancelIcon fontSize="small"/>
                            </Button>
                        </Tooltip>
                    </ButtonGroup>
                </ThemeProvider>
            </DialogTitle>
            <DialogActions sx={{paddingX: "20px", justifyContent: "flex-start", marginTop: "10px"}}>
                <ThemeProvider theme={buttonTheme}>
                    <ButtonGroup>
                    {
                        hideDefaultCommands ?
                        null
                        :
                        <Tooltip
                            title={saveActionCaption ?? "Simpan"}
                            arrow
                        >
                            <Button
                                variant="contained"
                                onClick={() => handleSave()}
                                disabled={!canSave}
                            >
                            {
                                props?.saveActionIcon ? <props.saveActionIcon fontSize="small" /> : <SaveIcon fontSize="small"/>
                            }
                            </Button>
                        </Tooltip>
                    }
                    {
                        customFormCommands?.map((c, idx) => {
                            return (
                                <Tooltip
                                    key={`modal_custom_command_${idx}`}
                                    title={c.title}
                                    arrow
                                >
                                    <Button
                                        variant="contained"
                                        onClick={() => c.action(formRef.current?.getData())}
                                        color={c.color ?? "dark"}
                                        sx={{marginLeft: `${(idx || !hideDefaultCommands) ? "2px" : "0"} !important`}}
                                    >
                                    {
                                        <c.icon fontSize="small"/>
                                    }
                                    </Button>
                                </Tooltip>
                            );
                        })
                    }
                    </ButtonGroup>
                </ThemeProvider>
            </DialogActions>
            <DialogContent sx={{...boxInputContainerStyle}}>
                <Box sx={{...boxInputStyle}}>
                    <Grid container spacing={2}>
                        <DataFieldInput
                            id="generic_modal"
                            fields={fields}
                            apiCaller={apiCaller}
                            data={formRef?.current?.getData() ?? data}
                            ref={formRef}
                            defaultFocusedField={currentFocusedField}
                        />
                    </Grid>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default GenericModal;
